import React, { useEffect, useState, useCallback } from "react";
import { Bell, BellOff, RefreshCw, Send, ShieldAlert } from "lucide-react";
import * as api from "../../api";
import { primeAudio, playChime } from "../../lib/notificationSound";

/* -----------------------------------------------------------------------------
 * AdminPushToggle — activate / deactivate Web Push notifications for the admin
 * on this device. Handles the full ceremony:
 *   1. Feature-detects service workers + Notifications API + PushManager.
 *   2. Registers `/sw.js`.
 *   3. Asks the user for permission.
 *   4. Subscribes via PushManager with the VAPID public key.
 *   5. Persists the subscription server-side (linked to the admin).
 *   6. Offers an "enviar prueba" button to fire a test notification.
 * --------------------------------------------------------------------------- */

const LS_ENDPOINT_KEY = "ag7_push_endpoint";

function b64UrlToUint8Array(b64) {
  const pad = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function AdminPushToggle({ showToast }) {
  const [supported, setSupported] = useState(true);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const ok =
      "serviceWorker" in navigator &&
      "PushManager" in window &&
      "Notification" in window;
    setSupported(ok);
    if (!ok) return;
    // Sync subscription state with the backend on mount.
    (async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration("/sw.js");
        const sub = reg ? await reg.pushManager.getSubscription() : null;
        if (sub) {
          const st = await api.pushStatus(sub.endpoint).catch(() => ({ subscribed: false }));
          setSubscribed(!!st.subscribed);
        } else {
          setSubscribed(false);
        }
      } catch (e) { console.warn("AdminPushToggle: subscription status check failed:", e); }
    })();
  }, []);

  const enable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Warm up AudioContext during this user gesture so subsequent chimes
      // (fired from a SW message) can play under browser autoplay policies.
      primeAudio();
      // 1. Ask permission
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        showToast?.("Permiso denegado", "No podremos enviarte notificaciones hasta que las habilites en el navegador.", "error");
        setBusy(false);
        return;
      }
      // 2. Fetch VAPID public key
      const { public_key, enabled } = await api.getPushVapidKey();
      if (!enabled || !public_key) throw new Error("El servidor no tiene VAPID configurado.");

      // 3. Register the service worker
      const reg = await navigator.serviceWorker.register("/sw.js");
      await navigator.serviceWorker.ready;

      // 4. Subscribe (or reuse existing)
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: b64UrlToUint8Array(public_key),
        });
      }
      const raw = sub.toJSON();
      // 5. Persist server-side
      await api.pushSubscribe({
        endpoint: raw.endpoint,
        keys: raw.keys,
        user_agent: navigator.userAgent || "",
      });
      try { localStorage.setItem(LS_ENDPOINT_KEY, raw.endpoint); }
      catch (e) { console.warn("AdminPushToggle: persist endpoint failed:", e); }
      setSubscribed(true);
      showToast?.("Notificaciones activadas", "Recibirás una alerta cada vez que entre un pedido nuevo.", "success");
    } catch (err) {
      showToast?.("No se pudieron activar", err.message || String(err), "error");
    } finally {
      setBusy(false);
    }
  }, [busy, showToast]);

  const disable = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration("/sw.js");
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      if (sub) {
        await api.pushUnsubscribe(sub.endpoint).catch(() => {});
        await sub.unsubscribe();
      }
      try { localStorage.removeItem(LS_ENDPOINT_KEY); }
      catch (e) { console.warn("AdminPushToggle: clear endpoint failed:", e); }
      setSubscribed(false);
      showToast?.("Notificaciones desactivadas", "Ya no recibirás alertas push en este dispositivo.", "success");
    } catch (err) {
      showToast?.("Error al desactivar", err.message || String(err), "error");
    } finally {
      setBusy(false);
    }
  }, [busy, showToast]);

  const sendTest = useCallback(async () => {
    if (sendingTest) return;
    setSendingTest(true);
    try {
      // Play the chime immediately in-app too, so the admin hears exactly
      // what will sound when a real push arrives.
      primeAudio();
      playChime();
      const res = await api.pushTest();
      showToast?.("Prueba enviada", `Enviadas ${res.sent} · Dispositivos limpiados ${res.removed}`, "success");
    } catch (err) {
      showToast?.("Error al probar", err.message || String(err), "error");
    } finally {
      setSendingTest(false);
    }
  }, [sendingTest, showToast]);

  if (!supported) {
    return (
      <div
        data-testid="admin-push-unsupported"
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl text-[11px] font-bold text-amber-300 border border-amber-500/40 bg-amber-500/10"
        title="Este navegador no soporta Web Push"
      >
        <ShieldAlert className="w-3.5 h-3.5" />
        <span className="hidden sm:inline">Push no soportado</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 sm:gap-2">
      <button
        onClick={subscribed ? disable : enable}
        disabled={busy}
        data-testid={subscribed ? "admin-push-disable-btn" : "admin-push-enable-btn"}
        title={subscribed ? "Desactivar notificaciones push" : "Activar notificaciones push"}
        aria-pressed={subscribed}
        className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 rounded-xl text-[11px] sm:text-xs font-bold whitespace-nowrap transition-all disabled:opacity-50 ${
          subscribed
            ? "text-emerald-300 border border-emerald-500/50 bg-emerald-500/10 hover:bg-emerald-500/20"
            : "text-amber-300 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20"
        }`}
      >
        {busy
          ? <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 animate-spin" />
          : subscribed
            ? <Bell className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            : <BellOff className="w-3.5 h-3.5 sm:w-4 sm:h-4" />}
        <span className="hidden sm:inline">{subscribed ? "Notif. Activadas" : "Activar Notif."}</span>
        <span className="sm:hidden">{subscribed ? "ON" : "OFF"}</span>
      </button>
      {subscribed && (
        <button
          onClick={sendTest}
          disabled={sendingTest}
          data-testid="admin-push-test-btn"
          title="Enviar notificación de prueba"
          aria-label="Enviar notificación de prueba"
          className="p-1.5 rounded-xl text-cyan-300 border border-cyan-500/40 bg-cyan-500/10 hover:bg-cyan-500/20 disabled:opacity-40"
        >
          {sendingTest ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
        </button>
      )}
    </div>
  );
}
